import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">Terms and Conditions</h1>
          <p className="text-gray-600 italic mb-8">Last updated: October 2025</p>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Welcome to <strong>Expected Value Club</strong> ("EVC," "we," "our," or "us").<br />
              By accessing or using this website (the "Site") and any related services, you agree to the following Terms and Conditions ("Terms").<br />
              If you do not agree to these Terms, please do not use this Site.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">1. Purpose of the Site</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Expected Value Club is a private online forum where members share and discuss prediction market ideas.<br />
              Content posted on the Site represents the personal views and analysis of individual members, not the opinions or recommendations of EVC.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              All materials, including text, data, comments, and analyses, are provided <strong>for informational and educational purposes only</strong>.<br />
              EVC is <strong>not</strong> a broker-dealer, investment adviser, or financial institution, and <strong>does not provide personalized investment, legal, tax, or financial advice</strong>.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">2. No Investment Advice or Solicitation</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">Nothing on this Site should be interpreted as:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>A recommendation to buy, sell, or hold any security or contract.</li>
              <li>An offer or solicitation to engage in any transaction.</li>
              <li>Advice tailored to your personal financial circumstances.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              Prediction markets and similar contracts are <strong>highly speculative</strong> and may result in the <strong>loss of your entire principal</strong>.<br />
              You are solely responsible for your own investment decisions and for verifying the accuracy of any information obtained from the Site.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">3. User Eligibility and Accounts</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">To become a member, you must:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Be at least 18 years old (or the age of majority in your jurisdiction).</li>
              <li>Provide accurate, current information during registration.</li>
              <li>Maintain the confidentiality of your account credentials.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              EVC reserves the right to accept, reject, or revoke membership at any time, for any reason, at its sole discretion.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">4. Member Submissions</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">By submitting ideas, comments, or other content ("Submissions"), you agree that:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>You are the original author of the Submission.</li>
              <li>You grant EVC a non-exclusive, royalty-free, perpetual license to display, distribute, and archive your content.</li>
              <li>You will not submit material that is defamatory, misleading, plagiarized, or violates any law or third-party rights.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              Moderators may edit, reject, or remove content that does not meet community standards or quality thresholds.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">5. Performance Tracking and Rankings</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Performance statistics, including ROI, rankings, and idea outcomes, are <strong>for illustrative and informational purposes only</strong>.<br />
              They do <strong>not</strong> represent actual investment returns, guarantees of accuracy, or verified financial performance.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              Historical results are <strong>not indicative of future performance</strong>.<br />
              Any calculations or rankings displayed on the Site are based on simplified models and may include assumptions, rounding, or delayed data.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">6. Risks and Disclaimers</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Prediction markets, derivatives, and speculative trading involve <strong>significant risk of loss</strong>.<br />
              Users should understand and accept the following risks:
            </p>

            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Markets may be illiquid or volatile.</li>
              <li>Settlement outcomes may differ from user expectations.</li>
              <li>Regulatory frameworks differ by jurisdiction.</li>
              <li>Loss of principal, partial or total, is possible.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              EVC assumes <strong>no responsibility</strong> for financial losses incurred by users acting on information found here.<br />
              Use of the Site is at your own risk.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">7. Limitation of Liability</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">To the fullest extent permitted by law:</p>

            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>EVC and its affiliates shall not be liable for any direct, indirect, incidental, special, or consequential damages arising out of or related to the use of this Site.</li>
              <li>This includes, without limitation, damages resulting from reliance on information obtained through the Site, loss of profits, or loss of data.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              EVC provides all content and services on an "<strong>as is</strong>" and "<strong>as available</strong>" basis, without warranties of any kind, express or implied.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">8. Community Conduct</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">Members agree to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Post only relevant, good-faith content.</li>
              <li>Refrain from harassment, spam, or manipulation of discussions.</li>
              <li>Respect confidentiality of member ideas.</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-4">
              EVC reserves the right to suspend or terminate accounts that violate these guidelines.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">9. Intellectual Property</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              All trademarks, logos, and site design elements are the property of Expected Value Club or its licensors.<br />
              You may not copy, reproduce, or distribute any part of the Site without express written consent.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              Members retain ownership of their original submissions but grant EVC the rights described in Section 4.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">10. Modifications to Terms</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              EVC reserves the right to update these Terms at any time without prior notice.<br />
              Your continued use of the Site after updates constitutes acceptance of the new Terms.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">11. Governing Law</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to its conflict of law provisions.
            </p>

            <hr className="my-8 border-gray-200" />

            <h2 className="text-2xl font-semibold text-gray-900 mt-10 mb-5">12. Contact</h2>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about these Terms or the Site, please contact:<br />
              📧 <strong><a href="mailto:legal@expectedvalueclub.com" className="text-primary-600 hover:text-primary-700">legal@expectedvalueclub.com</a></strong>
            </p>

            <hr className="my-8 border-gray-200" />

            <p className="text-center text-gray-600 mt-10 mb-6">
              © 2025 <strong>ExpectedValueClub.com</strong> — All rights reserved.
            </p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsPage;

